import cn from "classnames";
import { FC, MouseEventHandler, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useNavigate } from "react-router-dom";
import { usePopper } from "react-popper";
import { Devices, Facilities, FacilityGroups, OptionValue, Organizations, useAuth, Icons } from "@app/shared";
import { useFetch } from "../../hooks";
import { Pages } from "../pages";
import { DropDownMenuContainerId } from "./DropDownMenu";
import { LINK_BUTTON } from "./linkButton";

export type NavSelectorMode =
    | "orgs-only"
    | "orgs-groups"
    | "groups-locations"
    | "locations-devices";

type NavSelectorDropdownProps = {
    triggerLabel: string;
    mode: NavSelectorMode;
    initialOrgId?: string;
    initialGroupId?: string;
    initialFacilityId?: string;
    initialDeviceId?: string;
    className?: string;
};

const byLabel = (a: OptionValue, b: OptionValue) =>
    (a.label ?? "").localeCompare(b.label ?? "");

type SearchItem = { kind: string; sortOrder: number; label: string; id: string };

// The selector is a cascading "miller column" drill over the org hierarchy.
// A given page enters the drill at whichever level it has context for, and the
// user hovers to open the next level down, all the way to a device.
type Level = "orgs" | "groups" | "locations" | "devices";
const LEVEL_ORDER: Level[] = ["orgs", "groups", "locations", "devices"];
const LEVEL_TITLE: Record<Level, string> = {
    orgs: "Organizations",
    groups: "Location Groups",
    locations: "Locations",
    devices: "Devices",
};
const MODE_START_LEVEL: Record<NavSelectorMode, Level> = {
    "orgs-only": "orgs",
    "orgs-groups": "orgs",
    "groups-locations": "groups",
    "locations-devices": "locations",
};

const COLUMN_WIDTH_REM = 14;
// How long the mouse must rest on a row before we drill into it. The old 100ms
// was shorter than a deliberate sweep dwells per row, so crossing a list
// committed (and fetched) almost every row it passed over.
const HOVER_INTENT_MS = 200;
// A spinner only appears once loading outlasts this. Cache hits and fast
// responses settle first and paint nothing, which is what kills the flicker.
const SPINNER_DELAY_MS = 180;
// These are small name lists that change rarely, so a short TTL is enough to
// make re-entering a row you already visited instant.
const OPTIONS_TTL_MS = 30_000;
// A global `button` rule in styles/tailwind.css gives every button a border,
// shadow, background, padding and a fixed height. The inline Retry actions are
// text links, so they reset it.

const optionsCache = new Map<string, { at: number; items: OptionValue[] }>();
const optionsInFlight = new Map<string, Promise<OptionValue[]>>();

// Cache + in-flight dedupe in front of an options fetch. Keys are shared between
// the drill columns and the search lists, so warming one warms the other.
function cachedOptions(key: string, load: () => Promise<OptionValue[] | undefined>): Promise<OptionValue[]> {
    const hit = optionsCache.get(key);
    if (hit && Date.now() - hit.at < OPTIONS_TTL_MS) return Promise.resolve(hit.items);
    const flight = optionsInFlight.get(key);
    if (flight) return flight;              // a sweep can ask for the same key twice before the first lands
    const p = Promise.resolve(load())
        .then(items => {
            const list = items ?? [];
            optionsCache.set(key, { at: Date.now(), items: list });
            return list;
        })
        .finally(() => { optionsInFlight.delete(key); });
    optionsInFlight.set(key, p);
    return p;
}

// True only once `active` has stayed true for `delayMs` without interruption.
function useDelayedFlag(active: boolean, delayMs: number) {
    const [shown, setShown] = useState(false);
    useEffect(() => {
        if (!active) { setShown(false); return; }
        const t = setTimeout(() => setShown(true), delayMs);
        return () => clearTimeout(t);
    }, [active, delayMs]);
    return shown;
}

export const NavSelectorDropdown: FC<NavSelectorDropdownProps> = ({
    triggerLabel,
    mode,
    initialOrgId,
    initialGroupId,
    initialFacilityId,
    initialDeviceId,
    className,
}) => {
    const navigate = useNavigate();
    const { setCurrentOrganizationId } = useAuth();

    const [showMenu, setShowMenu] = useState(false);
    const [search, setSearch] = useState("");
    const isMouseOver = useRef(false);
    const popperButtonRef = useRef<HTMLDivElement>(null);
    const popperDivRef = useRef<HTMLDivElement>(null);
    const searchInputRef = useRef<HTMLInputElement>(null);
    const columnsRef = useRef<HTMLDivElement>(null);
    // Per-level refs for centering the active row when the menu opens.
    const scrollRefs = useRef<Partial<Record<Level, HTMLDivElement | null>>>({});
    const highlightRefs = useRef<Partial<Record<Level, HTMLDivElement | null>>>({});
    // True only while the menu is opening, so auto-centering runs once on open
    // and stops the moment the user starts hovering the list.
    const shouldCenterRef = useRef(false);
    const hoverTimerRef = useRef<ReturnType<typeof setTimeout>>();

    // The drilled-open item at each level. Deeper levels only render once their
    // parent has a selection here.
    const [selectedAt, setSelectedAt] = useState<Partial<Record<Level, string>>>({});

    const startLevel = MODE_START_LEVEL[mode];
    const startIdx = LEVEL_ORDER.indexOf(startLevel);
    const shows = useCallback((lvl: Level) => LEVEL_ORDER.indexOf(lvl) >= startIdx, [startIdx]);

    // Org context flows down the drill: when the orgs column is present the user
    // can browse into any org, otherwise everything is scoped to the current org.
    const activeOrgId = shows("orgs") ? selectedAt.orgs : initialOrgId;

    const popper = usePopper(popperButtonRef.current, popperDivRef.current, {
        placement: "bottom-start",
        modifiers: [
            { name: "offset", options: { offset: [0, 10] } },
            { name: "preventOverflow", options: { boundary: document.body, padding: 16 } },
            { name: "computeStyles", options: { adaptive: true, gpuAcceleration: true } },
            { name: "eventListeners", options: { scroll: showMenu, resize: showMenu } },
        ],
        strategy: "fixed",
    });

    // --- Per-level data fetching (one fetch per level, gated by its parent) ---
    const [orgItems, orgError, orgFetch] = useFetch(
        () => shows("orgs") ? cachedOptions("orgs:all", Organizations.getAllOptions) : Promise.resolve([]),
        [mode]
    );
    const [groupItems, groupError, groupFetch] = useFetch(
        () => shows("groups")
            ? cachedOptions(`groups:${activeOrgId ?? "all"}`, () => FacilityGroups.getAllFacilityGroupOptions(activeOrgId))
            : Promise.resolve([]),
        [mode, activeOrgId]
    );
    const [locationItems, locationError, locationFetch] = useFetch(
        () => {
            if (!shows("locations")) return Promise.resolve<OptionValue[]>([]);
            // Under a drilled group, or org-wide when locations is the entry level.
            if (selectedAt.groups)
                return cachedOptions(`locations:group:${selectedAt.groups}`, () =>
                    Facilities.getByGroupId(selectedAt.groups!)
                        .then(fs => (fs ?? []).map(f => ({ value: f.id, label: f.name } as OptionValue))));
            if (startLevel === "locations")
                return cachedOptions(`locations:org:${activeOrgId ?? "all"}`, () => Facilities.getOptions(activeOrgId));
            return Promise.resolve<OptionValue[]>([]);
        },
        [mode, selectedAt.groups, activeOrgId]
    );
    // Scoped by location only Access is enforced server-side
    const [deviceItems, deviceError, deviceFetch] = useFetch(
        () => (shows("devices") && selectedAt.locations)
            ? cachedOptions(`devices:location:${selectedAt.locations}`, () => Devices.getOptions(undefined, selectedAt.locations))
            : Promise.resolve([]),
        [mode, selectedAt.locations]
    );

    // --- Search data (all types) ---
    // Devices are the odd one out: filtered by org when an org is in context, but
    // fetched globally (all accessible devices) when there isn't one — so a super
    // admin on "/" can still search straight to any device. Gated on active search
    // text so the (potentially large) global list is only pulled on demand.
    const isSearching = search.trim().length > 0;
    const [searchOrgs, searchOrgsError, searchOrgsFetch] = useFetch(
        () => cachedOptions("orgs:all", Organizations.getAllOptions));
    const [searchGroups, searchGroupsError, searchGroupsFetch] = useFetch(
        () => cachedOptions("groups:all", () => FacilityGroups.getAllFacilityGroupOptions()));
    const [searchFacilities, searchFacilitiesError, searchFacilitiesFetch] = useFetch(
        () => cachedOptions(`locations:org:${initialOrgId ?? "all"}`, () => Facilities.getOptions(initialOrgId)), [initialOrgId]);
    const [searchDevices, searchDevicesError, searchDevicesFetch] = useFetch(
        () => isSearching
            ? cachedOptions(`devices:org:${initialOrgId ?? "all"}`, () => Devices.getOptions(initialOrgId))
            : Promise.resolve([]),
        [isSearching, initialOrgId]
    );

    // --- Sorted list per level ---
    const orgList = useMemo(() => [...(orgItems ?? [])].sort(byLabel), [orgItems]);
    const groupList = useMemo(() => [...(groupItems ?? [])].sort(byLabel), [groupItems]);
    const locationList = useMemo(() => [...(locationItems ?? [])].sort(byLabel), [locationItems]);
    const deviceList = useMemo(() => [...(deviceItems ?? [])].sort(byLabel), [deviceItems]);
    // A column needs more than its rows. While a fetch is in flight the rows are
    // still the *previous* parent's (useFetch keeps stale data), and a fetch that
    // failed leaves the list empty. Reporting "None available" in either case
    // reads as "this location has no devices", so each column carries its own
    // loading/error state and says which one it is.
    // Spinners are gated on the load actually being slow, so a cached column or a
    // fast response transitions straight to its rows with no intermediate paint.
    const orgSpinner = useDelayedFlag(orgFetch.isLoading, SPINNER_DELAY_MS);
    const groupSpinner = useDelayedFlag(groupFetch.isLoading, SPINNER_DELAY_MS);
    const locationSpinner = useDelayedFlag(locationFetch.isLoading, SPINNER_DELAY_MS);
    const deviceSpinner = useDelayedFlag(deviceFetch.isLoading, SPINNER_DELAY_MS);

    type ColumnState = { items: OptionValue[]; isLoading: boolean; showSpinner: boolean; error?: string; retry: () => void };
    const stateAt = (lvl: Level): ColumnState =>
        lvl === "orgs"
            ? { items: orgList, isLoading: orgFetch.isLoading, showSpinner: orgSpinner, error: orgError, retry: orgFetch.refreshData }
            : lvl === "groups"
                ? { items: groupList, isLoading: groupFetch.isLoading, showSpinner: groupSpinner, error: groupError, retry: groupFetch.refreshData }
                : lvl === "locations"
                    ? { items: locationList, isLoading: locationFetch.isLoading, showSpinner: locationSpinner, error: locationError, retry: locationFetch.refreshData }
                    : { items: deviceList, isLoading: deviceFetch.isLoading, showSpinner: deviceSpinner, error: deviceError, retry: deviceFetch.refreshData };

    // The item you are currently "at" (from the page context) per level.
    const highlightAt: Record<Level, string | undefined> = {
        orgs: initialOrgId,
        groups: initialGroupId,
        locations: initialFacilityId,
        devices: initialDeviceId,
    };

    // Columns shown right now: the entry level, then each deeper level whose
    // parent has been drilled into.
    const visibleLevels = useMemo(() => {
        const result: Level[] = [];
        for (let i = startIdx; i < LEVEL_ORDER.length; i++) {
            const lvl = LEVEL_ORDER[i];
            if (i === startIdx) { result.push(lvl); continue; }
            if (selectedAt[LEVEL_ORDER[i - 1]]) result.push(lvl); else break;
        }
        return result;
    }, [startIdx, selectedAt]);

    // --- Search results (grouped by type, sorted by name within) ---
    const searchResults = useMemo<SearchItem[]>(() => {
        if (!search) return [];
        const q = search.toLowerCase();
        const results: SearchItem[] = [];
        for (const o of (searchOrgs ?? []))
            if (o.label?.toLowerCase().includes(q))
                results.push({ kind: "Organization", sortOrder: 0, label: o.label!, id: o.value! });
        for (const g of (searchGroups ?? []))
            if (g.label?.toLowerCase().includes(q))
                results.push({ kind: "Location Group", sortOrder: 1, label: g.label!, id: g.value! });
        for (const f of (searchFacilities ?? []))
            if (f.label?.toLowerCase().includes(q))
                results.push({ kind: "Location", sortOrder: 2, label: f.label!, id: f.value! });
        for (const d of (searchDevices ?? []))
            if (d.label?.toLowerCase().includes(q))
                results.push({ kind: "Device", sortOrder: 3, label: d.label!, id: d.value! });
        results.sort((a, b) => a.sortOrder - b.sortOrder || a.label.localeCompare(b.label));
        return results;
    }, [search, searchOrgs, searchGroups, searchFacilities, searchDevices]);

    // Search matches across four independent fetches, so it can be partly loaded
    // or partly broken. Track which kinds are unusable so an incomplete result set
    // never looks like a complete one.
    const searchSources = [
        { kind: "organizations", error: searchOrgsError, isLoading: searchOrgsFetch.isLoading, retry: searchOrgsFetch.refreshData },
        { kind: "location groups", error: searchGroupsError, isLoading: searchGroupsFetch.isLoading, retry: searchGroupsFetch.refreshData },
        { kind: "locations", error: searchFacilitiesError, isLoading: searchFacilitiesFetch.isLoading, retry: searchFacilitiesFetch.refreshData },
        { kind: "devices", error: searchDevicesError, isLoading: searchDevicesFetch.isLoading, retry: searchDevicesFetch.refreshData },
    ];
    const loadingSearchKinds = searchSources.filter(s => s.isLoading && !s.error).map(s => s.kind);
    const showSearchLoading = useDelayedFlag(loadingSearchKinds.length > 0, SPINNER_DELAY_MS);
    const failedSearchSources = searchSources.filter(s => !!s.error);
    const retryFailedSearches = () => failedSearchSources.forEach(s => s.retry());

    // --- Highlight set for the full path (used in search results) ---
    const highlightSet = useMemo(() => {
        const s = new Set<string>();
        if (initialOrgId) s.add(initialOrgId);
        if (initialGroupId) s.add(initialGroupId);
        if (initialFacilityId) s.add(initialFacilityId);
        if (initialDeviceId) s.add(initialDeviceId);
        return s;
    }, [initialOrgId, initialGroupId, initialFacilityId, initialDeviceId]);

    // --- Open / close ---
    const closeMenu = useCallback(() => {
        clearTimeout(hoverTimerRef.current);
        shouldCenterRef.current = false;
        setShowMenu(false);
        setSearch("");
    }, []);

    const handleOutsideClick = useCallback(() => {
        if (!isMouseOver.current) closeMenu();
    }, [closeMenu]);

    const handleOtherDropdown = useCallback(() => { closeMenu(); }, [closeMenu]);

    useEffect(() => {
        if (showMenu) {
            document.addEventListener("click", handleOutsideClick);
            document.addEventListener("ddMenu:onShow", handleOtherDropdown);
            if (popper.forceUpdate) {
                const fn = popper.forceUpdate;
                setTimeout(() => fn?.(), 0);
            }
        }
        return () => {
            document.removeEventListener("click", handleOutsideClick);
            document.removeEventListener("ddMenu:onShow", handleOtherDropdown);
        };
    }, [showMenu, handleOutsideClick, handleOtherDropdown, popper.forceUpdate]);

    useEffect(() => {
        const timer = setTimeout(() => {
            if (popper.forceUpdate && showMenu) popper.forceUpdate();
        }, 50);
        return () => clearTimeout(timer);
    }, [showMenu, popper.forceUpdate]);

    // Reposition when the number of columns changes (menu width changes as the
    // user drills), and bring a freshly revealed column into view on narrow menus.
    useEffect(() => {
        if (!showMenu) return;
        const fn = popper.forceUpdate;
        fn?.();
        if (!search && !shouldCenterRef.current && columnsRef.current)
            columnsRef.current.scrollLeft = columnsRef.current.scrollWidth;
    }, [visibleLevels.length, showMenu, search, popper.forceUpdate]);

    useEffect(() => {
        if (showMenu) setTimeout(() => searchInputRef.current?.focus(), 60);
    }, [showMenu]);

    // Scroll each visible column so its currently-active row is centered when the
    // menu opens. Re-runs as async column data settles; stops once the user hovers.
    useEffect(() => {
        if (!showMenu || search || !shouldCenterRef.current) return;
        const centerInColumn = (container: HTMLDivElement | null | undefined, item: HTMLDivElement | null | undefined) => {
            if (!container || !item) return;
            const cRect = container.getBoundingClientRect();
            const iRect = item.getBoundingClientRect();
            const delta = (iRect.top - cRect.top) - container.clientHeight / 2 + iRect.height / 2;
            container.scrollTop += delta;
        };
        const timer = setTimeout(() => {
            if (!shouldCenterRef.current) return;
            for (const lvl of visibleLevels)
                centerInColumn(scrollRefs.current[lvl], highlightRefs.current[lvl]);
        }, 0);
        return () => clearTimeout(timer);
    }, [showMenu, search, visibleLevels, orgList, groupList, locationList, deviceList]);

    const handleToggle: MouseEventHandler = (e) => {
        e.stopPropagation();
        if (showMenu) { closeMenu(); return; }
        document.dispatchEvent(new Event("ddMenu:onShow"));
        shouldCenterRef.current = true;
        // Pre-expand the drill to the page's current path so all columns up to the
        // current entity are shown (and centered) on open.
        setSelectedAt({
            orgs: initialOrgId,
            groups: initialGroupId,
            locations: initialFacilityId,
        });
        setShowMenu(true);
    };

    // --- Navigation: a single click on any level opens that entity's page. ---
    const navigateLevel = (lvl: Level, id: string) => {
        if (lvl === "orgs") {
            setCurrentOrganizationId(id);
            navigate(Pages.ViewFacilityGroups.getUrl());
        } else if (lvl === "groups") {
            navigate(Pages.ViewFacilityGroup.getUrl({ id }));
        } else if (lvl === "locations") {
            navigate(Pages.FacilityDetails.getUrl({ id }));
        } else {
            navigate(Pages.DeviceDetails.getUrl({ id }));
        }
        closeMenu();
    };

    // --- Hover: open the next level down. Held for HOVER_INTENT_MS so sweeping the
    // mouse across a list doesn't drill (or fetch) per row. Terminal at devices. ---
    const hoverLevel = (lvl: Level, id: string) => {
        if (lvl === "devices") return;
        shouldCenterRef.current = false;
        if (selectedAt[lvl] === id) return;
        clearTimeout(hoverTimerRef.current);
        hoverTimerRef.current = setTimeout(() => {
            setSelectedAt(prev => {
                // Keep ancestors, set this level, drop everything deeper.
                const next: Partial<Record<Level, string>> = {};
                const idx = LEVEL_ORDER.indexOf(lvl);
                for (let i = 0; i < idx; i++) next[LEVEL_ORDER[i]] = prev[LEVEL_ORDER[i]];
                next[lvl] = id;
                return next;
            });
        }, HOVER_INTENT_MS);
    };

    const handleKeyDown = (e: React.KeyboardEvent) => {
        if (e.key === "Escape") { closeMenu(); e.stopPropagation(); }
    };

    // --- Column renderer ---
    const renderColumn = (lvl: Level) => {
        const { items, isLoading, showSpinner, error, retry } = stateAt(lvl);
        const noun = LEVEL_TITLE[lvl].toLowerCase();
        const isTerminal = lvl === "devices";
        const currentId = highlightAt[lvl];
        const expandedId = selectedAt[lvl];
        return (
            <div
                key={lvl}
                className="flex flex-col min-w-0 flex-shrink-0 border-r border-separators-page last:border-r-0"
                style={{ width: `${COLUMN_WIDTH_REM}rem` }}
            >
                <div className="border-b border-separators-page px-3 py-2 flex-shrink-0">
                    <span className="text-xs font-semibold uppercase tracking-wide text-muted-body-text">{LEVEL_TITLE[lvl]}</span>
                </div>
                <div
                    ref={(el) => { scrollRefs.current[lvl] = el; }}
                    className="overflow-y-auto flex-grow min-h-0"
                >
                    {isLoading ? (
                        showSpinner ? (
                            <div className="px-3 py-4 flex items-center justify-center gap-2 text-xs text-muted-body-text">
                                <Icons.Spinner size={12} className="animate-spin flex-shrink-0" />
                                <span>Loading {noun}...</span>
                            </div>
                        ) : null
                    ) : error ? (
                        <div className="px-3 py-4 flex flex-col items-center gap-1.5 text-center">
                            <Icons.Warning size={14} className="text-red-600 flex-shrink-0" />
                            <span className="text-xs text-red-600">Couldn't load {noun}.</span>
                            <button
                                type="button"
                                onClick={(e) => { e.stopPropagation(); retry(); }}
                                className={`text-xs text-link-page hover:text-link-page-hover underline ${LINK_BUTTON}`}
                            >
                                Retry
                            </button>
                        </div>
                    ) : items.length === 0 ? (
                        <div className="px-3 py-4 text-xs text-muted-body-text text-center">None available</div>
                    ) : null}
                    {!isLoading && !error && items.map((item) => {
                        const isCurrent = item.value === currentId;
                        const isExpanded = item.value === expandedId;
                        const showCaret = !isTerminal && (isCurrent || isExpanded);
                        return (
                            <div
                                key={item.value}
                                ref={isCurrent ? ((el) => { highlightRefs.current[lvl] = el; }) : undefined}
                                onClick={(e) => { e.stopPropagation(); navigateLevel(lvl, item.value!); }}
                                onMouseEnter={!isTerminal ? () => hoverLevel(lvl, item.value!) : undefined}
                                className={cn(
                                    "flex items-center gap-2 px-3 py-2 cursor-pointer transition-colors text-sm",
                                    isCurrent
                                        ? "bg-brand-primary bg-opacity-10 font-semibold"
                                        : isExpanded
                                            ? "bg-background-page-highlight-gray"
                                            : "hover:bg-background-page-highlight-gray"
                                )}
                            >
                                <span className="truncate flex-grow">{item.label}</span>
                                {showCaret && (
                                    <Icons.CaretRight
                                        size={10}
                                        className={cn("flex-shrink-0", isCurrent ? "text-brand-primary" : "text-muted-body-text")}
                                    />
                                )}
                            </div>
                        );
                    })}
                </div>
            </div>
        );
    };

    const renderSearchResults = () => (
        <div className="overflow-y-auto flex-grow min-h-0">
            {failedSearchSources.length > 0 && (
                <div className="px-4 py-3 flex items-start gap-2 border-b border-separators-page">
                    <Icons.Warning size={14} className="text-red-600 flex-shrink-0 mt-0.5" />
                    <div className="flex flex-col items-start gap-0.5 text-xs">
                        <span className="text-red-600">
                            Couldn't search {joinKinds(failedSearchSources.map(s => s.kind))}.
                        </span>
                        <span className="text-muted-body-text">Matches there are missing from this list.</span>
                        <button
                            type="button"
                            onClick={(e) => { e.stopPropagation(); retryFailedSearches(); }}
                            className={`text-link-page hover:text-link-page-hover underline ${LINK_BUTTON}`}
                        >
                            Retry
                        </button>
                    </div>
                </div>
            )}
            {loadingSearchKinds.length > 0 && showSearchLoading && (
                <div className="px-4 py-3 flex items-center gap-2 text-xs text-muted-body-text border-b border-separators-page">
                    <Icons.Spinner size={12} className="animate-spin flex-shrink-0" />
                    <span>Still loading {joinKinds(loadingSearchKinds)}...</span>
                </div>
            )}
            {searchResults.length === 0 && loadingSearchKinds.length === 0 && failedSearchSources.length === 0 && (
                <div className="px-4 py-6 text-sm text-muted-body-text text-center">No results found</div>
            )}
            {searchResults.map((item, idx) => {
                const prevKind = idx > 0 ? searchResults[idx - 1].kind : null;
                const showHeader = item.kind !== prevKind;
                return (
                    <div key={`${item.kind}-${item.id}`}>
                        {showHeader && (
                            <div className="px-4 pt-3 pb-1 text-xs font-semibold uppercase tracking-wide text-muted-body-text">
                                {item.kind}s
                            </div>
                        )}
                        <div
                            onClick={(e) => { e.stopPropagation(); navigateLevel(searchKindToLevel(item.kind), item.id); }}
                            className={cn(
                                "flex items-center gap-3 px-4 py-2 cursor-pointer transition-colors text-sm",
                                highlightSet.has(item.id)
                                    ? "bg-brand-primary bg-opacity-10 font-semibold"
                                    : "hover:bg-background-page-highlight-gray"
                            )}
                        >
                            <span className="truncate flex-grow">{item.label}</span>
                        </div>
                    </div>
                );
            })}
        </div>
    );

    const popoverWidth = search
        ? "min(28rem, calc(100vw - 2rem))"
        : `min(${(visibleLevels.length * COLUMN_WIDTH_REM)+2}rem, calc(100vw - 2rem))`;

    const renderPopover = () => (
        <div
            ref={popperDivRef}
            onClick={(e) => e.stopPropagation()}
            onKeyDown={handleKeyDown}
            style={{
                ...popper.styles.popper,
                zIndex: 999,
                visibility: showMenu ? "visible" : "hidden",
                width: popoverWidth,
            }}
            className="flex flex-col border border-separators-page shadow-lg absolute bg-dialog-background z-100 rounded-lg overflow-hidden"
            {...popper.attributes.popper}
        >
            <div className="p-2 border-b border-separators-page">
                <div className="relative">
                    <Icons.Search className="absolute left-2.5 top-1/2 -translate-y-1/2 text-muted-body-text pointer-events-none" size={14} />
                    <input
                        ref={searchInputRef}
                        // paddingLeft is set inline because the shared `input.omnisight`
                        // rule (px-2) out-specifies the pl-8 utility and would otherwise
                        // let the text run under the search icon.
                        style={{ paddingLeft: "2.25rem" }}
                        className="omnisight w-full pr-3 py-1.5 text-sm"
                        placeholder="Search..."
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        onKeyDown={(e) => {
                            if (e.key === "Escape" && search) { setSearch(""); e.stopPropagation(); }
                        }}
                    />
                </div>
            </div>
            <div ref={columnsRef} className="flex overflow-x-auto" style={{ height: "min(22rem, 60vh)" }}>
                {search ? renderSearchResults() : visibleLevels.map(renderColumn)}
            </div>
        </div>
    );

    const el = document.getElementById(DropDownMenuContainerId);

    return (
        <div
            onMouseEnter={() => { isMouseOver.current = true; }}
            onMouseLeave={() => { isMouseOver.current = false; }}
            className={cn("h-full flex flex-row items-center", className)}
        >
            <div
                ref={popperButtonRef}
                onClick={handleToggle}
                className="cursor-pointer text-link-page hover:text-link-page-hover my-auto flex items-center gap-1.5"
            >
                <span className="truncate max-w-[12rem]">{triggerLabel}</span>
                <Icons.CaretDown size={10} className={cn("transition-transform", showMenu && "rotate-180")} />
            </div>
            {el ? createPortal(renderPopover(), el) : renderPopover()}
        </div>
    );
};

const joinKinds = (kinds: string[]) =>
    kinds.length <= 1
        ? (kinds[0] ?? "")
        : `${kinds.slice(0, -1).join(", ")} and ${kinds[kinds.length - 1]}`;

const searchKindToLevel = (kind: string): Level =>
    kind === "Organization" ? "orgs"
        : kind === "Location Group" ? "groups"
            : kind === "Location" ? "locations"
                : "devices";
